/**
 * Asset Performance Telemetry
 * ---------------------------------------------------------------
 * Behavioral media intelligence console for the Viral tab.
 * Reads real `content_metrics` joined to `content_assets`, fills
 * gaps with deterministic synthetic baselines, computes
 * engagement_score, and surfaces top performers, format
 * intelligence and archetype intelligence.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity, Radar, Trophy, Layers, Brain, Loader2 } from "lucide-react";
import {
  calculateEngagementScore,
  engagementBand,
  syntheticBaseline,
  type AssetMetricsRow,
} from "@/lib/contentMetrics";

interface AssetMetaRow {
  id: string;
  hook: string | null;
  scenario_title: string;
  archetype: string;
  format: string;
  status: string;
}

interface MergedAsset extends AssetMetaRow {
  metrics: Partial<AssetMetricsRow>;
  engagement_score: number;
}

const fmt = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`;

const bandColor: Record<"low" | "medium" | "high", string> = {
  low: "border-muted-foreground/40 text-muted-foreground",
  medium: "border-amber-400/50 text-amber-300",
  high: "border-emerald-400/60 text-emerald-300",
};

export default function AssetPerformanceTelemetry() {
  const [rows, setRows] = useState<MergedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const tickRef = useRef<number>(0);
  const [, force] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: assets }, { data: metrics }] = await Promise.all([
      (supabase as any)
        .from("content_assets")
        .select("id, hook, scenario_title, archetype, format, status")
        .order("created_at", { ascending: false })
        .limit(200),
      (supabase as any)
        .from("content_metrics")
        .select(
          "content_asset_id, views, likes, comments, shares, saves, impressions, watch_time, completion_rate, copied_responses, exports, publish_count, engagement_score"
        ),
    ]);

    const byId = new Map<string, Partial<AssetMetricsRow>>();
    for (const m of (metrics ?? []) as any[]) {
      byId.set(m.content_asset_id, m);
    }

    const merged: MergedAsset[] = ((assets ?? []) as AssetMetaRow[]).map((a) => {
      const real = byId.get(a.id);
      // Real wins, synthetic fills gaps. A row of all-zeros is still
      // treated as "warming up" and gets the baseline floor.
      const baseline = syntheticBaseline(a.id);
      const isCold =
        !real ||
        ((real.views ?? 0) +
          (real.impressions ?? 0) +
          (real.shares ?? 0) +
          (real.exports ?? 0) ===
          0);
      const m: Partial<AssetMetricsRow> = isCold
        ? baseline
        : { ...baseline, ...real };
      return {
        ...a,
        metrics: m,
        engagement_score: calculateEngagementScore(m),
      };
    });

    setRows(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---------- Subtle simulated live signal ----------
  // Bumps a couple counters every ~6s on a deterministic rotation. Local-only,
  // never written to DB — keeps the console feeling operational without noise.
  useEffect(() => {
    if (rows.length === 0) return;
    const id = window.setInterval(() => {
      tickRef.current += 1;
      setRows((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const i = tickRef.current % next.length;
        const j = (tickRef.current * 7) % next.length;
        const bumpView = { ...next[i] };
        const m1 = { ...bumpView.metrics };
        m1.views = (m1.views ?? 0) + 1;
        m1.impressions = (m1.impressions ?? 0) + 2;
        bumpView.metrics = m1;
        bumpView.engagement_score = calculateEngagementScore(m1);
        next[i] = bumpView;

        if (tickRef.current % 5 === 0 && next[j]) {
          const bumpExp = { ...next[j] };
          const m2 = { ...bumpExp.metrics };
          m2.exports = (m2.exports ?? 0) + 1;
          bumpExp.metrics = m2;
          bumpExp.engagement_score = calculateEngagementScore(m2);
          next[j] = bumpExp;
        }
        return next;
      });
      force((x) => x + 1);
    }, 6000);
    return () => window.clearInterval(id);
  }, [rows.length]);

  // ---------- Aggregations ----------
  const top = useMemo(
    () => [...rows].sort((a, b) => b.engagement_score - a.engagement_score).slice(0, 5),
    [rows]
  );

  const formatStats = useMemo(() => {
    const groups: Record<string, MergedAsset[]> = {};
    for (const r of rows) {
      const k = (r.format || "unknown").toLowerCase();
      (groups[k] ||= []).push(r);
    }
    return Object.entries(groups).map(([format, list]) => {
      const avgEng =
        list.reduce((s, r) => s + r.engagement_score, 0) / list.length;
      const avgComp =
        list.reduce((s, r) => s + Number(r.metrics.completion_rate ?? 0), 0) /
        list.length;
      const exports = list.reduce(
        (s, r) => s + Number(r.metrics.exports ?? 0),
        0
      );
      return {
        format,
        count: list.length,
        avgEngagement: Math.round(avgEng),
        avgCompletion: Math.round(avgComp),
        exports,
      };
    }).sort((a, b) => b.avgEngagement - a.avgEngagement);
  }, [rows]);

  const archetypeStats = useMemo(() => {
    const groups: Record<string, MergedAsset[]> = {};
    for (const r of rows) {
      const k = r.archetype || "unknown";
      (groups[k] ||= []).push(r);
    }
    return Object.entries(groups).map(([archetype, list]) => {
      const avgEng =
        list.reduce((s, r) => s + r.engagement_score, 0) / list.length;
      const fmtCounts: Record<string, number> = {};
      for (const r of list) {
        const f = (r.format || "unknown").toLowerCase();
        fmtCounts[f] = (fmtCounts[f] ?? 0) + 1;
      }
      const topFormat = Object.entries(fmtCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
      return {
        archetype,
        count: list.length,
        avgEngagement: Math.round(avgEng),
        topFormat: topFormat ?? "—",
      };
    }).sort((a, b) => b.avgEngagement - a.avgEngagement).slice(0, 8);
  }, [rows]);

  const maxFmtEng = Math.max(1, ...formatStats.map((f) => f.avgEngagement));

  // ---------- Render ----------
  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="space-y-1">
        <CardTitle className="text-lg flex items-center gap-2">
          <Radar className="w-5 h-5 text-cyan" />
          Asset Performance Telemetry
          <Badge
            variant="outline"
            className="ml-2 text-[10px] uppercase tracking-wider border-cyan/40 text-cyan font-mono"
          >
            {rows.length} signals
          </Badge>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-emerald-300/80">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            live
          </span>
        </CardTitle>
        <p className="text-[11px] font-mono text-muted-foreground">
          Behavioral signal monitoring · engagement-weighted intelligence layer
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Calibrating telemetry…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-xs font-mono text-muted-foreground">
            No assets generated yet. Telemetry will populate as the queue fills.
          </div>
        ) : (
          <>
            {/* Per-asset metrics matrix */}
            <Section icon={<Activity className="w-3.5 h-3.5" />} label="Asset Signal Matrix">
              <ScrollArea className="h-[280px] -mx-2">
                <div className="px-2 divide-y divide-glass-border">
                  {rows.slice(0, 80).map((r) => {
                    const m = r.metrics;
                    const band = engagementBand(r.engagement_score);
                    return (
                      <div
                        key={r.id}
                        className="grid grid-cols-12 gap-2 py-2 items-center text-[11px] font-mono"
                      >
                        <div className="col-span-12 md:col-span-4 min-w-0">
                          <div className="text-[12px] text-foreground truncate font-sans">
                            {r.hook || r.scenario_title}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {r.archetype} · {r.format}
                          </div>
                        </div>
                        <Stat label="impr" value={fmt(m.impressions ?? 0)} />
                        <Stat label="views" value={fmt(m.views ?? 0)} />
                        <Stat label="watch" value={`${fmt(m.watch_time ?? 0)}s`} />
                        <Stat label="comp" value={`${Math.round(Number(m.completion_rate ?? 0))}%`} />
                        <Stat label="shr" value={fmt(m.shares ?? 0)} />
                        <Stat label="sav" value={fmt(m.saves ?? 0)} />
                        <Stat label="copy" value={fmt(m.copied_responses ?? 0)} />
                        <Stat label="exp" value={fmt(m.exports ?? 0)} />
                        <div className="col-span-6 md:col-span-1 flex md:justify-end">
                          <Badge
                            variant="outline"
                            className={`text-[10px] uppercase tracking-wider font-mono ${bandColor[band]}`}
                          >
                            {r.engagement_score}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </Section>

            {/* Top performing */}
            <Section icon={<Trophy className="w-3.5 h-3.5" />} label="Top Performing Assets">
              <div className="grid grid-cols-1 gap-2">
                {top.map((r, i) => {
                  const band = engagementBand(r.engagement_score);
                  return (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 py-2 px-3 rounded border border-glass-border bg-charcoal-deep/40"
                    >
                      <div className="text-[10px] font-mono text-cyan w-5 shrink-0">
                        #{i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-foreground truncate">
                          {r.hook || r.scenario_title}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground truncate">
                          {r.scenario_title} · {r.format}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground hidden md:block">
                        comp {Math.round(Number(r.metrics.completion_rate ?? 0))}%
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground hidden md:block">
                        exp {fmt(r.metrics.exports ?? 0)}
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider font-mono ${bandColor[band]}`}
                      >
                        {r.engagement_score}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Format intelligence */}
            <Section icon={<Layers className="w-3.5 h-3.5" />} label="Format Performance">
              <div className="space-y-2">
                {formatStats.map((f) => {
                  const w = Math.max(4, (f.avgEngagement / maxFmtEng) * 100);
                  return (
                    <div key={f.format} className="space-y-1">
                      <div className="flex items-center justify-between text-[11px] font-mono">
                        <span className="uppercase tracking-wider text-foreground/90">
                          {f.format}
                        </span>
                        <span className="text-muted-foreground">
                          eng {f.avgEngagement} · comp {f.avgCompletion}% · exp {f.exports} · n{f.count}
                        </span>
                      </div>
                      <div className="h-1.5 bg-charcoal-deep rounded-sm overflow-hidden border border-glass-border">
                        <div
                          className="h-full bg-cyan/70"
                          style={{ width: `${w}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Archetype intelligence */}
            <Section icon={<Brain className="w-3.5 h-3.5" />} label="Top Manipulation Archetypes">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {archetypeStats.map((a) => {
                  const band = engagementBand(a.avgEngagement);
                  return (
                    <div
                      key={a.archetype}
                      className="flex items-center justify-between gap-3 py-2 px-3 rounded border border-glass-border bg-charcoal-deep/40"
                    >
                      <div className="min-w-0">
                        <div className="text-[12px] text-foreground capitalize truncate">
                          {a.archetype.replace(/_/g, " ")}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {a.count} assets · top: {a.topFormat}
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider font-mono ${bandColor[band]}`}
                      >
                        {a.avgEngagement}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </Section>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- internals ----------

function Section({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono text-cyan">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="col-span-3 md:col-span-1 flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-[11px] text-foreground/90">{value}</span>
    </div>
  );
}
